import React, { useEffect, useState } from 'react';
import { Button } from 'reactstrap';
import Load, { Charge, Invoice as InvoiceModel, InvoiceItem } from '../../../models/load';
import Loader from '../../UI/Loader/Loader';
import classes from './Invoice.module.scss';
import get from '../../../services/axios/get';
import post from '../../../services/axios/post';
import { format } from 'date-fns';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { customAlert } from '../../../store/actions/alert';
import generateAndDownloadInvoice, { InvoiceData } from '../InvoicePdfGenerator/InvoicePdfGenerator';

interface InvoiceProps {
  load: Load;
  close: () => void;
  onDownloadInvoice?: () => void;
  onDeleteInvoice?: () => void;
  onRefreshLoads?: () => void;
  gensetPrice?: number;
}

interface InvoiceDefaults {
  companyName: string;
  address: string;
  vatNumber: string;
  currency: string;
  issueDate: string;
  dueDate: string;
  email: string;
  contactNumber: string;
}

interface PriceListEntry {
  entityType: string;
  id: string;
  [date: string]: any;
}

const Invoice: React.FC<InvoiceProps> = (props) => {
  const { load, close, onDownloadInvoice, onDeleteInvoice, onRefreshLoads, gensetPrice } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [invoiceDefaults, setInvoiceDefaults] = useState<InvoiceDefaults | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [priceList, setPriceList] = useState<any>(null);
  const [invoiceData, setInvoiceData] = useState<InvoiceModel | null>(null);
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.auth.user);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {

        if (load.invoiceId) {
          const invoiceExists = await fetchInvoice(load.invoiceId);
            if (!invoiceExists) {
              await fetchPriceList();
            }
        }

        await fetchInvoiceDefaults();
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [load.invoiceId]);

  useEffect(() => {
    if (invoiceData) {
      setInvoiceNumber(invoiceData.invoiceNumber);
      setNotes(invoiceData.notes);
    } else if (!invoiceNumber) {
      setInvoiceNumber(generateInvoiceNumber());
    }
  }, [invoiceData, invoiceNumber]);

  const fetchInvoice = (invoiceId: string): Promise<boolean> => {
    return new Promise<boolean>((resolve, reject) => {
      get(
        `/invoice/${invoiceId}`,
        {},
        (res) => {
          if (res.data) {
            setInvoiceData(res.data);
            resolve(true);
          } else {
            resolve(false);
          }
        },
        (err) => {
          resolve(false);
        }
      );
    });
  };

  const fetchInvoiceDefaults = () => {
    return new Promise<void>((resolve, reject) => {
      const params: Record<string, string> = {}

      if (user?.userType === 'admin') {
        params['loadId'] = load.id;
      }

      get(
        '/invoice-defaults',
        params,
        (res) => {
          setInvoiceDefaults(res.data);
          resolve();
        },
        (err) => {
          dispatch(customAlert(true, 'Failed to load invoice defaults'));
          reject(err);
        }
      );
    });
  };

  const fetchPriceList = () => {
    return new Promise<void>((resolve, reject) => {
      get(
        '/price-list/subcontractors',
        {},
        (res) => {
          const parsedList = extractPriceListObjects([res.data]);
          setPriceList(parsedList['Sub Contractors'] || {});
          resolve();
        },
        (err) => {
          dispatch(customAlert(true, 'Failed to load price list data'));
          reject(err);
        }
      );
    });
  };

  function extractPriceListObjects(data: PriceListEntry[]): Record<string, any> {
    const result: Record<string, any> = {};

    data?.forEach((entry) => {
      const clientName = entry.clientName;
      const dates: Record<string, any> = {}

      if (entry.WC != null) {
        Object.keys(entry.WC).forEach((key) => {
          dates[key] = { ...entry.WC[key], ...dates?.[key] }
        })
      }

      if (entry.EC != null) {
        Object.keys(entry.EC).forEach((key) => {
          dates[key] = { ...entry.EC[key], ...dates?.[key] }
        })
      }

      result[clientName] = dates
    });

    return result;
  }

  function formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function extractPriceListDateForDate(data?: any, date?: string): any {
    let relevantDate: Date | null = null;

    if (data == null || date == null) {
      return null;
    }

    Object.keys(data).forEach((key) => {
      const priceListDate = new Date(key);
      const loadDateObject = new Date(date);

      priceListDate.setHours(0, 0, 0, 0);
      loadDateObject.setHours(0, 0, 0, 0);

      if (
        priceListDate <= loadDateObject && (
          relevantDate == null ||
          relevantDate < priceListDate
        )) {
        relevantDate = priceListDate;
      }
    });

    if (relevantDate == null) {
      return null;
    }

    const relevantDateString = formatDate(relevantDate);

    return data[relevantDateString];
  }

  const generateInvoiceNumber = () => {
    const today = new Date();
    const dateStr = format(today, 'yyyyMMdd');
    const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `INV-${dateStr}-${randomNum}`;
  };

  const getSubPrice = (): number => {
    // If we have invoice data, use it instead of calculating
    if (invoiceData) {
      const transportItem = invoiceData.items.find(item => 
        item.description.includes('Transport Service'));
      return transportItem ? transportItem.amount : 0;
    }

    if (load.subPrice) {
      return parseFloat(load.subPrice.replace(/[^0-9.-]+/g, '')) || 0;
    }

    if (load.loadPoint1) {
      const subPriceList = extractPriceListDateForDate(priceList, load.loadDate);
      if (subPriceList && subPriceList[load.loadPoint1]) {
        let subPriceNumber = subPriceList[load.loadPoint1];
        if (subPriceList.netOverrides?.[load.loadPoint1] != null) {
          subPriceNumber = subPriceList.netOverrides[load.loadPoint1];
        } else if (subPriceNumber != null) {
          let multiplier = 1;
          if (subPriceList.fuelLevyPercentage != null) {
            multiplier += (subPriceList.fuelLevyPercentage / 100);
          }
          subPriceNumber = parseFloat((subPriceNumber * multiplier).toFixed(2));
          return subPriceNumber;
        }
        return parseFloat(subPriceNumber) || 0;
      }
    }

    return 0;
  };

  const getCharges = (): Charge[] => {
    try {
      return JSON.parse(load.charges || '[]');
    } catch (e) {
      return [];
    }
  };

  const getChargesTotal = (): number => {
    // If we have invoice data, calculate from invoice items excluding transport and genset
    if (invoiceData) {
      const excludedDescriptions = ['Transport Service', 'Genset'];
      return invoiceData.items
        .filter(item => !excludedDescriptions.some(desc => item.description.includes(desc)))
        .reduce((sum, item) => sum + item.amount, 0);
    }

    const charges = getCharges();
    return charges.reduce((sum, charge) => {
      const amount = parseFloat(charge.amount?.toString().replace(/[^0-9.-]+/g, '') || '0');
      return sum + (isNaN(amount) ? 0 : amount * (charge.quantity || 1));
    }, 0);
  };

  const getTotalAmount = (): number => {
    // If we have invoice data, use the total amount directly
    if (invoiceData) {
      return invoiceData.items.reduce((sum, item) => sum + item.amount, 0);
    }

    return getSubPrice() + (gensetPrice || 0) + getChargesTotal();
  };

  const prepareInvoiceItems = (): InvoiceItem[] => {
    // If we have invoice data, use those items directly
    if (invoiceData) {
      return invoiceData.items;
    }

    const items: InvoiceItem[] = [];

    if (getSubPrice() > 0) {
      items.push({
        description: `Transport Service ${load.loadPoint1 ? `from ${load.loadPoint1}` : ''}${load.loadPoint2 ? ` to ${load.loadPoint2}` : ''}`,
        quantity: 1,
        unitPrice: getSubPrice(),
        amount: getSubPrice()
      });
    }

    if (load.isGenset && gensetPrice && gensetPrice > 0) {
      items.push({
        description: 'Genset',
        quantity: 1,
        unitPrice: gensetPrice,
        amount: gensetPrice
      });
    }

    const charges = getCharges();
    charges.forEach(charge => {
      const amount = parseFloat(charge.amount?.toString().replace(/[^0-9.-]+/g, '') || '0');
      if (!isNaN(amount) && amount > 0) {
        items.push({
          description: charge.description || 'Additional Charge',
          quantity: charge.quantity || 1,
          unitPrice: amount,
          amount: amount * (charge.quantity || 1)
        });
      }
    });

    return items;
  };

  const generateInvoice = () => {
    setIsSaving(true);

    const payload = {
      invoiceNumber: invoiceNumber,
      loadId: load.id,
      client: typeof load.driver === 'object' ? load.driver?.ownerName : 'Owner',
      clientAddress: '',  // In a real system, this would come from a database
      loadNumber: load.clientRef || '',
      containerNumber: load.containerNumber || '',
      loadDate: load.loadDate,
      items: prepareInvoiceItems(),
      notes: notes,
      dueDate: invoiceDefaults?.dueDate || '',
      issueDate: invoiceDefaults?.issueDate || format(new Date(), 'yyyy-MM-dd'),
      totalAmount: getTotalAmount()
    };

    post(
      '/invoice',
      payload,
      (res) => {
        setIsSaving(false);
        if (onRefreshLoads) {
          onRefreshLoads();
        }
        handleDownloadInvoice();
        close();
      },
      (err) => {
        dispatch(customAlert(true, 'Failed to generate invoice'));
        setIsSaving(false);
      }
    );
  };

  const deleteInvoice = () => {
    if (onDeleteInvoice) {
      onDeleteInvoice();
    }
    if (onRefreshLoads) {
      onRefreshLoads();
    }
    close();
  };

  const handleDownloadInvoice = () => {
    setIsDownloading(true);

    if (onDownloadInvoice) {
      onDownloadInvoice();
      setIsDownloading(false);
      return;
    }

    const invoiceData: InvoiceData = {
      invoiceNumber: invoiceNumber,
      issueDate: invoiceDefaults?.issueDate || format(new Date(), 'yyyy-MM-dd'),
      companyName: invoiceDefaults?.companyName || '',
      companyAddress: invoiceDefaults?.address || '',
      vatNumber: invoiceDefaults?.vatNumber || '',
      email: invoiceDefaults?.email || '',
      contactNumber: invoiceDefaults?.contactNumber || '',
      gensetPrice: gensetPrice || 0,
      clientAddress: '',
      loadDetails: {
        loadNumber: load.clientRef || '',
        containerNumber: load.containerNumber || '',
        loadDate: load.loadDate ? format(new Date(load.loadDate), 'dd/MM/yyyy') : '',
        loadPoint1: load.loadPoint1 || '',
        loadPoint2: load.loadPoint2 || ''
      },
      items: prepareInvoiceItems(),
      notes: notes,
      totalAmount: getTotalAmount()
    };

    generateAndDownloadInvoice(invoiceNumber, invoiceData, {
      onComplete: () => {
        if (onRefreshLoads) {
          onRefreshLoads();
        }
        setIsDownloading(false);
      },
      onError: (message) => {
        dispatch(customAlert(true, message));
        setIsDownloading(false);
      }
    });
  };

  // Check if a load has been invoiced by checking if invoiceData exists
  const isInvoiced = invoiceData != null && load.invoiceId != null && load.invoiceId !== '';

  if (isLoading) {
    return (
      <div className={classes.loaderContainer}>
        <Loader size="medium" />
      </div>
    );
  }

  return (
    <div className={classes.invoiceContainer}>
      <h4 className={classes.title}>Invoice</h4>

      <div className={classes.invoiceHeader}>
        <div className={classes.companyDetails}>
          <div className={classes.companyName}>{invoiceDefaults?.companyName || ''}</div>
          <div className={classes.companyAddress}>{invoiceDefaults?.address || ''}</div>
          <div className={classes.companyInfo}>VAT: {invoiceDefaults?.vatNumber || ''}</div>
          <div className={classes.companyInfo}>Tel: {invoiceDefaults?.contactNumber || ''}</div>
          <div className={classes.companyInfo}>Email: {invoiceDefaults?.email || ''}</div>
        </div>

        <div className={classes.invoiceDetails}>
          <div className={classes.invoiceNumberContainer}>
            <label>Invoice Number:</label>
            <input
              type="text"
              className={classes.invoiceNumberInput}
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              readOnly={isInvoiced}
            />
          </div>

          <div className={classes.dateInfo}>
            <div>Issue Date: {invoiceData?.issueDate || invoiceDefaults?.issueDate || format(new Date(), 'yyyy-MM-dd')}</div>
          </div>
        </div>
      </div>

      <div className={classes.clientInfo}>
        <h6>Bill To:</h6>
        <div>{invoiceData?.client || (typeof load.driver === 'object' ? load.driver?.ownerName : 'Owner')}</div>
      </div>

      <div className={classes.loadInfo}>
        <div className={classes.loadDetail}>
          <span className={classes.label}>Load Ref:</span>
          <span>{invoiceData?.loadNumber || load.clientRef || '-'}</span>
        </div>
        <div className={classes.loadDetail}>
          <span className={classes.label}>Container:</span>
          <span>{invoiceData?.containerNumber || load.containerNumber || '-'}</span>
        </div>
        <div className={classes.loadDetail}>
          <span className={classes.label}>Load Date:</span>
          <span>
            {invoiceData?.loadDate 
              ? format(new Date(invoiceData.loadDate), 'dd/MM/yyyy') 
              : (load.loadDate ? format(new Date(load.loadDate), 'dd/MM/yyyy') : '-')}
          </span>
        </div>
      </div>

      <table className={classes.invoiceTable}>
        <thead>
          <tr>
            <th>Description</th>
            <th>Qty</th>
            <th>Unit Price</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {prepareInvoiceItems().map((item, index) => (
            <tr key={index}>
              <td>{item.description}</td>
              <td>{item.quantity}</td>
              <td>{invoiceDefaults?.currency || 'R'} {item.unitPrice.toFixed(2)}</td>
              <td>{invoiceDefaults?.currency || 'R'} {item.amount.toFixed(2)}</td>
            </tr>
          ))}
          <tr className={classes.totalRow}>
            <td colSpan={3}>Total</td>
            <td>{'R'} {getTotalAmount().toFixed(2)}</td>
          </tr>
        </tbody>
      </table>

      <div className={classes.notesSection}>
        <label>Notes:</label>
        <textarea
          className={classes.notesTextarea}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          readOnly={isInvoiced}
        />
      </div>

      <div className={classes.actionButtons}>
        <Button className={classes.cancelButton} onClick={close}>
          Cancel
        </Button>

        {isInvoiced && onDeleteInvoice && (
          <Button
            className={classes.deleteButton}
            onClick={deleteInvoice}
          >
            Delete Invoice
          </Button>
        )}

        {isInvoiced ? (
          <Button
            className={classes.themeGreenButton}
            onClick={handleDownloadInvoice}
            disabled={isDownloading}
          >
            {isDownloading ? 'Downloading...' : 'Download Invoice'}
          </Button>
        ) : (
          <Button
            className={classes.themeGreenButton}
            onClick={generateInvoice}
            disabled={isSaving}
          >
            {isSaving ? 'Generating...' : 'Generate Invoice'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default Invoice;
